import type { FrameworkClass, FrameworkInitialization } from './framework.d';

export const findFrameworkInitialization = (
  parentClass: FrameworkClass | undefined,
  childClass: FrameworkClass | undefined,
  parameter: string
): FrameworkInitialization | undefined => {
  const primary = parentClass ?? childClass;
  if (!primary?.initialization) return undefined;

  const matchIn = (fc: FrameworkClass, useChild: FrameworkClass | undefined): FrameworkInitialization | undefined =>
    fc.initialization.find((item: FrameworkInitialization) => {
      if (item.parameter !== parameter) return false;
      if (!useChild) return true;
      if (item.class != null) {
        return item.class === useChild;
      }
      return fc === useChild;
    });

  if (childClass) {
    const direct = matchIn(primary, childClass);
    if (direct) return direct;
    if (!parentClass) {
      const relaxed = matchIn(childClass, undefined);
      if (relaxed) return relaxed;
    }
  }

  return primary.initialization.find((item: FrameworkInitialization) => item.parameter === parameter);
};

export const parseFrameworkValue = (value: unknown, type: string): string | number => {
  const raw = String(value).trim();
  const t = (type || '').toLowerCase();
  if (t === 'int' || t === 'integer') {
    const isIntegerLiteral = /^[+-]?\d+$/.test(raw);
    if (isIntegerLiteral) {
      const n = parseInt(raw, 10);
      if (!isNaN(n)) return n;
    }
  }
  if (t === 'float' || t === 'double' || t === 'number') {
    // Only coerce plain numeric literals to numbers. Expressions (e.g. "1 / 2") must remain strings so
    // callers can interpret them correctly.
    const isNumericLiteral = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(raw);
    if (isNumericLiteral) {
      const n = parseFloat(raw);
      if (!isNaN(n)) return n;
    }
  }
  // Strip quotes for strings if present; otherwise return raw
  if ((raw.startsWith('"') && raw.endsWith('"')) || (raw.startsWith("'") && raw.endsWith("'"))) {
    return raw.slice(1, -1);
  }
  return raw;
};

export const getFrameworkDefaultValue = (
  parentClass: FrameworkClass | undefined,
  childClass: FrameworkClass | undefined,
  parameter: string,
  resolveParameterValue?: (parameter: string) => unknown
): string | number | null => {
  const initParam = findFrameworkInitialization(parentClass, childClass, parameter);
  if (initParam?.defaultValueByParameter) {
    const { parameter: dependentParameter, values } = initParam.defaultValueByParameter;
    const resolvedValue =
      resolveParameterValue?.(dependentParameter) ??
      (dependentParameter === parameter ? null : getFrameworkDefaultValue(parentClass, childClass, dependentParameter, resolveParameterValue));
    const conditionalDefault = resolvedValue == null ? undefined : values[String(resolvedValue)];
    if (conditionalDefault != null) {
      return parseFrameworkValue(conditionalDefault, String(initParam.type || ''));
    }
  }
  if (initParam?.value != null) {
    return parseFrameworkValue(initParam.value, String(initParam.type || ''));
  }
  return null;
};
